import { useEffect, useRef, useState } from 'react'
import { useStableLiveQuery } from './useStableLiveQuery'

/**
 * Like {@link useStableLiveQuery}, but **pauses** the underlying Dexie
 * subscription while `isActive` is false. Inactive tabs in the TabStack
 * then don't re-run queries / memos / reconciliation on every unrelated
 * Dexie write.
 *
 * Behaviour:
 * - `isActive === true`: runs the query live, returns stable refs.
 * - `isActive === false`: returns the last known result from when the page
 *   was active. No subscription is held; unrelated writes are ignored.
 * - On the `false → true` flip, a fresh query runs once so the page catches
 *   up with anything it missed while paused (Dexie local reads are typically
 *   <10 ms, so there's no visible lag).
 *
 * Caveat: if the tab is activated and the user sees the cached result for
 * one paint before the fresh query commits, that's acceptable for task /
 * project lists — staleness window is a few frames.
 *
 * @example
 * const tasks = useActivePausedLiveQuery(
 *   () => db.tasks.filter(t => t.projectId === id).toArray(),
 *   [id],
 *   isActive,
 * )
 */
export function useActivePausedLiveQuery<T>(
  querier: () => T | Promise<T>,
  deps: unknown[],
  isActive: boolean,
): T | undefined {
  // Cached "last known" result captured while active — returned during the
  // paused window so downstream `useMemo` sees a stable reference.
  const cachedRef = useRef<T | undefined>(undefined)

  // Bump a local nonce whenever we want the hook to re-run its querier. Used
  // to trigger a fresh catch-up query on the false → true edge.
  const [catchUpTick, setCatchUpTick] = useState(0)
  const prevActiveRef = useRef(isActive)
  useEffect(() => {
    if (!prevActiveRef.current && isActive) {
      setCatchUpTick(t => t + 1)
    }
    prevActiveRef.current = isActive
  }, [isActive])

  // Two branches for the querier:
  //  - active: real query
  //  - paused: resolve immediately with the cached value
  // Either way the subscription exists (Dexie-react-hooks needs it), but the
  // paused branch is independent of any Dexie table, so writes don't re-fire
  // it. `deps` include `isActive` + `catchUpTick` so the subscription
  // restarts on activation.
  const fresh = useStableLiveQuery<T | undefined>(
    () => (isActive ? querier() : Promise.resolve(cachedRef.current)),
    [...deps, isActive, catchUpTick],
  )

  if (isActive && fresh !== undefined) {
    cachedRef.current = fresh
  }

  return isActive ? fresh : cachedRef.current
}
